/**
 * Plain-language copy for the public Weather perspective.
 * Keep honest: do not call satellite precipitation "radar".
 * Trust labels and source cards derive from weatherSourceTaxonomy.js.
 */

import {
  WEATHER_TRUST_LABEL_DEFINITIONS,
  resolveTransparencySourceCards,
} from "./weatherSourceTaxonomy.js";

export { WEATHER_TRUST_LABEL_DEFINITIONS };

/** @deprecated Use WEATHER_TRUST_LABEL_DEFINITIONS — kept for existing imports */
export const WEATHER_TRUST_LABEL_GLOSSARY = WEATHER_TRUST_LABEL_DEFINITIONS;

/** Source cards for the public transparency page (taxonomy-backed). */
export const WEATHER_TRANSPARENCY_SOURCE_SECTIONS = resolveTransparencySourceCards();

export const WEATHER_PERSPECTIVE_PRINCIPLE =
  "The Weather perspective should explain the weather context without requiring the user to understand radar products, ArcGIS services, source health codes, or fallback logic.";

export const WEATHER_HELP_INTRO =
  "Use this view to understand current rain, local sensors, and weather-risk context near a place. Official sources are preferred when available. Backup and satellite layers are clearly labeled when used.";

export const WEATHER_HELP_SECTIONS = [
  {
    id: "radar",
    title: "Official rain / radar layer",
    body: "Shows where precipitation is active using Colombia’s official IDEAM layer when it is available and fresh.",
  },
  {
    id: "backup",
    title: "Backup radar",
    body: "RainViewer is a third-party backup used when the official layer is unavailable or for animated context.",
  },
  {
    id: "satellite",
    title: "Satellite estimate",
    body: "A satellite-based precipitation estimate — useful where radar is limited, but it is an estimate, not radar.",
  },
  {
    id: "siata",
    title: "Local SIATA sensors",
    body: "Ground-based rain gauges, stream levels, and alerts from the SIATA network in Medellín and Valle de Aburrá.",
  },
  {
    id: "risk",
    title: "Weather risk context",
    body: "A plain-language summary of rain, streams, and alerts near a selected map point or property.",
  },
  {
    id: "freshness",
    title: "Source freshness",
    body: "Labels like “Updated recently” or “May be outdated” describe how current each source is. Warnings stay visible on purpose.",
  },
];

export const WEATHER_SOURCE_EXPLAINERS = {
  ideam_radar_composite: {
    title: "Official IDEAM radar composite",
    body: "Official IDEAM radar composite for Colombia. Useful for seeing areas of active precipitation.",
    badgeTone: "official",
    badgeLabel: "Official",
  },
  ideam_precipitation_intensity: {
    title: "Official IDEAM precipitation intensity",
    body: "Official IDEAM precipitation intensity layer for Colombia. Useful for seeing where rain is likely occurring.",
    badgeTone: "official",
    badgeLabel: "Official",
  },
  ideam_unknown: {
    title: "Official IDEAM weather layer",
    body: "Official IDEAM weather layer for Colombia. The exact product type is still being validated.",
    badgeTone: "official",
    badgeLabel: "Official",
  },
  rainviewer: {
    title: "RainViewer backup radar",
    body: "Third-party radar backup. Used when official data is unavailable or for animated radar context.",
    badgeTone: "backup",
    badgeLabel: "Backup",
  },
  satellite: {
    title: "Satellite precipitation estimate",
    body: "Satellite precipitation estimate. Useful where radar coverage is limited, but it is an estimate, not radar.",
    badgeTone: "estimate",
    badgeLabel: "Estimate",
  },
  siata_rain: {
    title: "SIATA rain gauges",
    body: "Local rain sensors from the SIATA monitoring network for Medellín and Valle de Aburrá.",
    badgeTone: "local",
    badgeLabel: "Local sensors",
  },
  siata_quebrada: {
    title: "SIATA quebrada levels",
    body: "Local stream and quebrada level sensors. Useful for local flood-awareness context.",
    badgeTone: "local",
    badgeLabel: "Local sensors",
  },
  siata_air: {
    title: "SIATA air quality",
    body: "Local air quality readings from SIATA where available in the map view.",
    badgeTone: "local",
    badgeLabel: "Local sensors",
  },
  siata_alerts: {
    title: "SIATA alerts",
    body: "Active environmental and hydrology alerts published through SIATA for the region.",
    badgeTone: "local",
    badgeLabel: "Local alerts",
  },
  siata_santa_elena_radar: {
    title: "SIATA Santa Elena Radar",
    body: "Local radar for Medellín and the Aburrá Valley when cached frames are available.",
    badgeTone: "localOfficial",
    badgeLabel: "Local official",
  },
  weather_risk: {
    title: "Weather risk context",
    body: "A plain-language summary of current weather signals near a selected place.",
    badgeTone: "info",
    badgeLabel: "Summary",
  },
};

export const WEATHER_LAYER_GROUPS = {
  radar: {
    id: "radar",
    title: "Current rain / radar",
    description: "Official Colombia precipitation imagery with optional backup radar and animation.",
    defaultOpen: true,
  },
  satellite: {
    id: "satellite",
    title: "Satellite estimate",
    description: "Supplemental satellite precipitation estimate — not ground-based radar.",
    defaultOpen: false,
  },
  localSensors: {
    id: "localSensors",
    title: "Local sensors (SIATA)",
    description: "Ground-based rain, stream, air quality, and alert data for Valle de Aburrá.",
    defaultOpen: false,
  },
  risk: {
    id: "risk",
    title: "Risk context",
    description: "Weather risk summary for the map center or a point you select on the map.",
    defaultOpen: true,
  },
  reference: {
    id: "reference",
    title: "Reference & optional layers",
    description: "Flood zones, rainfall models, forecasts, and community context layers.",
    defaultOpen: false,
  },
};

export const WEATHER_RISK_LEGEND = [
  { swatch: "#2e7d32", label: "Low", description: "Low current weather signal" },
  { swatch: "#f9a825", label: "Moderate", description: "Moderate weather signal" },
  { swatch: "#ef6c00", label: "Elevated", description: "Elevated weather concern" },
  { swatch: "#c62828", label: "High", description: "High weather concern" },
  { swatch: "#9e9e9e", label: "Unknown", description: "Insufficient fresh data" },
];

export const WEATHER_SATELLITE_LEGEND_NOTE =
  "Satellite colors estimate precipitation intensity and may differ from radar or ground sensors.";

export const WEATHER_RADAR_LEGEND_NOTE =
  "Color scale depends on the active data source. Official IDEAM and RainViewer backup use different palettes.";

export const WEATHER_SIATA_RAIN_LEGEND_NOTE =
  "Gauge colors reflect recent rainfall intensity categories from SIATA station readings.";

export const WEATHER_SIATA_QUEBRADA_LEGEND_NOTE =
  "Stream colors reflect relative level status from SIATA hydrology sensors.";

export const WEATHER_NO_DATA_POINT_MESSAGE =
  "No fresh weather data is available for this point right now.";

export const WEATHER_MAP_CLICK_HINT =
  "Tap anywhere on the map to see weather risk and source context for that location.";

/** Plain-language radar source status (no raw keys, URLs, or provider internals). */
export const WEATHER_RADAR_SOURCE_STATUS_COPY = {
  officialActive: "Radar source: Official radar",
  officialBartActive: "Radar source: IDEAM Imagen Compuesta",
  legacyOspaActive: "Radar source: Legacy IDEAM OSPA",
  backupActive: "Radar source: RainViewer backup",
  backupSelected: "Radar source: RainViewer backup",
  fallbackActive: "Official radar unavailable. Showing RainViewer backup.",
  officialNoFrames: "Official radar selected, but no renderable frames are available.",
  unavailable: "No renderable radar source is available.",
  updatedRecently: "Radar updated recently.",
  mayBeStale: "Official radar may be stale.",
  fallbackShowing: "Official radar unavailable. Showing RainViewer backup.",
  fallbackReady: "Backup radar is available if official radar becomes unavailable.",
  turnedOff: "Radar is turned off.",
  outOfView: "Radar is available, but not for the current map view.",
  satelliteEstimateNote:
    "Satellite estimate shown for context. This is not ground-based radar.",
  siataValleyActive: "Showing SIATA Santa Elena Radar · Local official",
  valleyIdeamOutsideSiataCoverage:
    "Showing IDEAM official radar · Outside SIATA local coverage",
  valleyIdeamSiataUnavailable:
    "Showing IDEAM official radar · SIATA local radar unavailable",
  valleyRainviewerBackup:
    "Showing RainViewer backup · Official radar unavailable",
  siataOnlyUnavailable:
    "SIATA local radar unavailable · No fallback active",
};

export const WEATHER_SIATA_SANTA_ELENA_RADAR_COPY = {
  toggleDescription:
    "Local radar for Medellín and the Aburrá Valley. Toggle independently from national radar layers.",
  helper: "Local radar for Medellín and the Aburrá Valley.",
  coverageNote: "Best for local valley context when available.",
  authorityNote:
    "Radar helps show rain patterns. Official alerts come from alert feeds, not radar pixels alone.",
  mobileLabel: "SIATA radar",
  mobileBadge: "Local",
  unavailable: "SIATA local radar is not available right now.",
  waiting: "Waiting for latest SIATA radar frame.",
  latestOnly: "Latest SIATA radar frame",
  animationUnavailable: "Animation frames not available yet.",
  stale: "SIATA radar is stale.",
  showingLatest: "Showing latest available SIATA frame.",
};

export const WEATHER_AEROCIVIL_AVIATION_RADAR_COPY = {
  toggleDescription:
    "Official aeronautical radar from Aerocivil. Independent from Valley Preferred and national IDEAM composite.",
  helper: "Aeronautical radar for airports and regional coverage.",
  coverageNote: "Useful near Bogotá (GTA) and national aeronautical mosaic — not Medellín local radar.",
  authorityNote:
    "Aeronautical radar supports situational context. Official alerts come from alert feeds, not radar pixels alone.",
  mobileLabel: "Aerocivil radar",
  mobileBadge: "Official",
  unavailable: "Aerocivil aeronautical radar is not available right now.",
  waiting: "Waiting for latest Aerocivil radar frame.",
  latestOnly: "Latest Aerocivil radar frame",
  animationUnavailable: "Animation frames not available yet.",
  stale: "Aerocivil radar is stale.",
  showingLatest: "Showing latest available Aerocivil frame.",
  attribution: "Radar imagery: Aerocivil — Meteorología Aeronáutica para Colombia.",
};

export const VALLEY_PREFERRED_ROLLOUT_COPY = {
  recommendedBadge: "Recommended in Aburrá Valley",
  recommendedHelper:
    "Uses SIATA local radar when available, then IDEAM official radar, then RainViewer backup.",
  outsideCoverageNote: "Best inside Aburrá Valley coverage.",
  firstUseTitle: "Valley Preferred radar",
  firstUseBody:
    "Using Valley Preferred radar: SIATA local radar is shown when available in the Aburrá Valley. IDEAM official radar and RainViewer backup remain available.",
  useRecommendedAction: "Use recommended source",
  resetToRecommendedAction: "Reset to recommended",
};

/** Resident-facing Weather Source Transparency page (public route). */
export const WEATHER_TRANSPARENCY_PAGE = {
  path: "/weather/sources",
  title: "Weather data sources",
  eyebrow: "Source transparency",
  subtitle:
    "Midpoint Map combines official, local, backup, estimate, and community weather context so residents can understand what they are seeing on the map.",
  intro:
    "This page explains what each label means, how sources are chosen, when fallbacks apply, and what Midpoint Map does not do.",
  principle:
    "We label sources honestly. When official data is unavailable, backup or estimate layers are shown clearly — never as official warnings.",
  sourcesLinkLabel: "About sources",
  sourcesLinkAriaLabel: "Learn about weather data sources",
};

export const WEATHER_TRANSPARENCY_SAFETY_FOOTER =
  "Midpoint Map provides weather context and source transparency. It does not replace official emergency alerts, instructions from authorities, or professional judgment during severe weather.";

export const WEATHER_TRANSPARENCY_VALLEY_SWITCH = {
  title: "Why the map may switch sources",
  body: "In Medellín and the Aburrá Valley, Midpoint Map can prefer SIATA local radar when it is available. If it is unavailable, the map may show IDEAM official radar or RainViewer backup. The source label tells you what is currently shown.",
  items: [
    "Valley Preferred uses SIATA Santa Elena Radar when the map is inside local coverage and SIATA is healthy.",
    "Falls back to IDEAM BART when SIATA is unavailable or outside local coverage.",
    "Falls back to RainViewer backup when official or local radar is unavailable.",
    "The map always shows which source is actually being rendered.",
  ],
};

export const WEATHER_TRANSPARENCY_FALLBACK = {
  title: "How radar fallback works",
  body: "When you use Preferred or Valley Preferred radar, Midpoint Map tries sources in order. RainViewer backup appears only when official sources are unavailable or when you select it manually — and it is always labeled Backup.",
  steps: [
    { label: "Valley Preferred", detail: "SIATA local radar → IDEAM official → RainViewer backup" },
    { label: "Preferred", detail: "IDEAM official → RainViewer backup when needed" },
    { label: "Manual selection", detail: "You can choose a specific source; labels stay honest" },
  ],
};

export const WEATHER_TRANSPARENCY_RADAR_VS_ALERT = {
  title: "Radar context is not the same as an official alert",
  lead: "Radar imagery helps show rain patterns. Official alerts come from official alert feeds and local authorities. Midpoint Map does not create official emergency warnings from radar pixels.",
  bullets: [
    "Radar can support context about rain near a place.",
    "Stream and gauge sensors can support risk context.",
    "Official and local alert feeds are the authority for official alerts.",
    "During emergencies, follow instructions from local authorities.",
  ],
};

export const WEATHER_TRANSPARENCY_FRESHNESS = {
  title: "Freshness and fallback",
  intro: "Status labels on the map describe how current a source is and whether a fallback is active.",
  examples: [
    { label: "Updated X min ago", example: "SIATA local radar · Updated 5 min ago" },
    { label: "Stale", example: "Official radar may be stale" },
    { label: "Unavailable", example: "SIATA local radar unavailable" },
    { label: "Fallback active", example: "RainViewer backup · Official radar unavailable" },
    { label: "Latest frame", example: "Showing latest available SIATA frame" },
    { label: "Replay mode", example: "Historical replay — live refresh paused" },
  ],
};

export const WEATHER_TRANSPARENCY_COVERAGE = {
  title: "Coverage and limits",
  items: [
    "IDEAM national radar covers Colombia at composite scale — useful for broad rain patterns.",
    "SIATA Santa Elena Radar focuses on Medellín and the Aburrá Valley when local frames are available.",
    "Satellite precipitation estimates cover wider areas but are estimates, not ground-based radar.",
    "Local SIATA sensors and alerts reflect ground monitoring in the Valle de Aburrá network.",
    "Data freshness varies by source. Labels like “Updated recently” or “May be outdated” describe currentness.",
    "Historical SIATA replay uses a short-term rolling archive for review — not unlimited storage.",
  ],
};

export const WEATHER_TRANSPARENCY_ATTRIBUTION = {
  title: "Attribution",
  items: [
    { label: "IDEAM", text: "Official radar and precipitation products where applicable (Instituto de Hidrología, Meteorología y Estudios Ambientales)." },
    { label: "SIATA", text: "Local sensors, alerts, and Santa Elena radar imagery where applicable (SIATA / Área Metropolitana del Valle de Aburrá)." },
    { label: "RainViewer", text: "Third-party backup radar when official layers are unavailable." },
    { label: "Satellite estimates", text: "Satellite-derived precipitation estimates where configured — labeled Estimate, not radar." },
  ],
};

